/**
 * Shared Assessments step used by every intake/follow-up typed form that
 * administers questionnaires in Connections. Owns the questionnaire
 * selector, simple cross-questionnaire prefill (PHQ-4 → PHQ-9/GAD-7), the
 * C-SSRS recommendation alert (when PHQ-9 Q9 > 0), and the questionnaire
 * renderer panel.
 *
 * Pulled out of the legacy `Encounter.form.tsx` so the typed forms compose
 * it directly without duplicating the JSX.
 *
 * @agentic connections
 */

import { Alert, Group, Stack, Switch, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormReturnType } from "@mantine/form";
import type { Encounter } from "@medplum/fhirtypes";

import { QUESTIONNAIRE_URLS } from "@benchmark/core/constants";
import { isConfirmedAdult } from "@benchmark/core/utils";
import {
  composeAdaptiveScreener,
  composePersistedAdaptiveSession,
  evaluateQuestionnaireCompletion,
  isExplicitSettlement,
  isScreenerOffLocked,
  isSelfHarmPositive,
  TRIGGER_ADDED_SECTIONS,
  type AdaptiveSectionKey,
  type AdaptiveSectionReasonCode,
  type AdaptiveSectionStatus,
} from "@benchmark/core/services";
import { useAdaptiveScreenerMemory } from "@/hooks/domain/useAdaptiveScreenerMemory";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { QuestionnaireRenderer } from "@/components/shared/forms/QuestionnaireRenderer";
import { QuestionnaireSelector } from "@/components/shared/forms/QuestionnaireSelector";
import { AdaptiveScreenerStep } from "@/components/domain/encounter/shared/AdaptiveScreenerStep";
import type {
  ClinicalFollowUpInteractionFormData,
  ClinicalIntakeInteractionFormData,
  CoCMFollowUpInteractionFormData,
  CoCMIntakeInteractionFormData,
  EncounterQuestionnaireResponseEntry,
} from "@benchmark/core/types";

/**
 * Pair-wise prefill: copy PHQ-4 answers into PHQ-9 / GAD-7 questionnaires
 * when those are newly selected, without overwriting existing answers.
 *
 * @agentic connections
 */
const SIMPLE_PREFILL = {
  [QUESTIONNAIRE_URLS.PHQ9]: [
    { fromUrl: QUESTIONNAIRE_URLS.PHQ4, from: "phq4-1", to: "phq9-1" },
    { fromUrl: QUESTIONNAIRE_URLS.PHQ4, from: "phq4-2", to: "phq9-2" },
  ],
  [QUESTIONNAIRE_URLS.GAD7]: [
    { fromUrl: QUESTIONNAIRE_URLS.PHQ4, from: "phq4-3", to: "gad7-1" },
    { fromUrl: QUESTIONNAIRE_URLS.PHQ4, from: "phq4-4", to: "gad7-2" },
  ],
} as const;

/**
 * Sections enabled for the current release (D15). Suicide-risk and safety-plan
 * are trigger-only and are not part of the base enabled set. Everyday
 * functioning (WSAS) is now enabled — previously withheld pending licence (D3).
 * @agentic connections
 */
const RELEASE_ENABLED_SECTIONS = new Set<AdaptiveSectionKey>([
  "patient-rating",
  "depression",
  "anxiety",
  "loneliness",
  "functioning",
  "alcohol",
  "drug-use",
]);

/** Form shapes that use the Assessments step. @agentic connections */
type AssessmentsCapableFormData =
  | ClinicalFollowUpInteractionFormData
  | ClinicalIntakeInteractionFormData
  | CoCMFollowUpInteractionFormData
  | CoCMIntakeInteractionFormData;

/**
 * Shared validator that fails the Assessments step when any selected
 * questionnaire is missing or incomplete. Reused by every typed form that
 * mounts {@link AssessmentsStep}.
 *
 * @agentic connections
 */
export const validateAssessmentsStep = (
  values: AssessmentsCapableFormData,
): Record<string, string> | null => {
  const duplicateScoreUrls = new Set(
    values._questionnaireDuplicateScoreUrls ?? [],
  );
  const isIgnorableDuplicate = (url: string) =>
    url !== QUESTIONNAIRE_URLS.C_SSRS && duplicateScoreUrls.has(url);

  // When the adaptive screener is active, recording is gated on every section
  // having settled and any positive suicide-risk screen being acknowledged
  // (C6 + D13). The rail computes this and stores it on the form.
  const adaptive = values as AssessmentsCapableFormData & {
    _adaptiveActive?: boolean;
    _adaptiveCanRecord?: boolean;
  };
  if (adaptive._adaptiveActive) {
    return adaptive._adaptiveCanRecord
      ? null
      : {
          assessments:
            "Answer or settle every section, and acknowledge any suicide-risk notice, before recording the screening.",
        };
  }

  const selected = (values.selectedQuestionnaires ?? []).filter(
    (url) => !isIgnorableDuplicate(url),
  );
  const responses = values.questionnaireResponses ?? {};
  const settlements = values._adaptiveSettlements ?? {};
  const urlsToValidate = new Set([
    ...selected,
    ...Object.entries(responses)
      .filter(([, entry]) => entry.responses.length > 0)
      .map(([url]) => url),
  ]);
  const incomplete = [...urlsToValidate].filter((url) => {
    if (isIgnorableDuplicate(url)) return false;
    const settlement = settlements[url] as AdaptiveSectionStatus | undefined;
    if (settlement && isExplicitSettlement(settlement)) {
      return false;
    }
    const response = responses[url];
    if (!response) {
      return true;
    }
    const completion = evaluateQuestionnaireCompletion(
      url,
      response.responses,
      { declaredComplete: response.completed },
    );
    return !completion.supported || !completion.complete;
  });
  if (incomplete.length === 0) return null;
  return {
    assessments:
      "Please complete all selected questionnaires before proceeding. All required questions must be answered.",
  };
};

/**
 * Props for {@link AssessmentsStep}.
 *
 * @agentic connections
 */
export type AssessmentsStepProps<T extends AssessmentsCapableFormData> = {
  form: UseFormReturnType<T>;
  disabled?: boolean;
  /**
   * The patient's FHIR birth date. Required to enable the adults-only adaptive
   * screener (D14 fail-safe): when absent or under 18, the adaptive rail never
   * shows and the existing questionnaire picker is used, unchanged.
   */
  patientBirthDate?: string;
  /** The patient's id, used to resolve cross-visit adaptive-screener memory. */
  patientId?: string;
  /** Existing/scribe Encounter excluded from prior memory. */
  currentEncounterId?: string;
  /** Locks the adaptive on/off state after finalization. */
  currentEncounterStatus?: Encounter["status"];
};

/**
 * Assessments step: questionnaire selector, simple prefill, C-SSRS alert,
 * and the rendered questionnaire panel.
 *
 * @agentic connections
 */
export const AssessmentsStep = <T extends AssessmentsCapableFormData>({
  form,
  disabled = false,
  patientBirthDate,
  patientId,
  currentEncounterId,
  currentEncounterStatus,
}: AssessmentsStepProps<T>) => {
  const [cssrsAlertDismissed, setCssrsAlertDismissed] = useState(false);

  // Adults-only adaptive screener (D10/D14). A missing or under-18 birth date
  // fails safe to the existing picker flow.
  const confirmedAdult = isConfirmedAdult(patientBirthDate);
  const currentValues = form.values as AssessmentsCapableFormData;
  const scoreConflictSignature = JSON.stringify(
    [...(currentValues._questionnaireScoreConflictUrls ?? [])].sort(),
  );
  const duplicateScoreSignature = JSON.stringify(
    [...(currentValues._questionnaireDuplicateScoreUrls ?? [])].sort(),
  );
  const conflictedQuestionnaireUrls = useMemo(
    () => new Set(currentValues._questionnaireScoreConflictUrls ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scoreConflictSignature],
  );
  const duplicateScoreQuestionnaireUrls = useMemo(
    () => new Set(currentValues._questionnaireDuplicateScoreUrls ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [duplicateScoreSignature],
  );
  const hasScoreConflicts = conflictedQuestionnaireUrls.size > 0;
  const selectedQuestionnaires = currentValues.selectedQuestionnaires ?? [];
  const questionnaireResponses = currentValues.questionnaireResponses ?? {};
  const persistedSettlements = (currentValues._adaptiveSettlements ??
    {}) as Record<string, AdaptiveSectionStatus>;
  const persistedSession = currentValues._adaptiveScreenerSession as
    | {
        id: string;
        baseline: boolean;
        reasons: Record<string, AdaptiveSectionReasonCode>;
      }
    | undefined;
  const hasPersistedSession = Boolean(persistedSession?.id);
  const hasExistingEncounter = currentEncounterStatus !== undefined;
  const hasRestoredSession = Boolean(
    hasExistingEncounter && persistedSession?.id,
  );
  // Existing classic encounters remain classic. Adaptive default-on applies
  // only to a genuinely new sitting or an encounter carrying adaptive
  // provenance.
  const adaptiveActive =
    confirmedAdult && (!hasExistingEncounter || hasPersistedSession);

  // Cross-visit memory composes only a new sitting. A persisted session already
  // owns its original composition and must not ingest itself or later visits.
  const memory = useAdaptiveScreenerMemory(
    patientId,
    currentEncounterId,
    adaptiveActive && !hasRestoredSession,
  );
  // Reuse a persisted session id on draft resume; otherwise mint one for this
  // sitting. `crypto.randomUUID` is available in every supported browser and
  // the Node 22 test runtime, so no insecure fallback is needed.
  const persistedSessionId = persistedSession?.id;
  const [adaptiveSessionId] = useState(
    () => persistedSessionId ?? crypto.randomUUID(),
  );

  const freshComposition = composeAdaptiveScreener({
    isConfirmedAdult: confirmedAdult,
    isBaseline: memory.isBaseline,
    openedInFullBefore: memory.openedInFullBefore,
    // BMH-650 §8.2/§9.6: every follow-up asks the change rating (PGI-C),
    // regardless of whether a prior severity rating (PGI-S) was recorded — so
    // this is keyed off follow-up status, not the severity anchor.
    ratingAnchorEstablished: !memory.isBaseline,
    enabledSections: RELEASE_ENABLED_SECTIONS,
  });

  // Suicide risk is re-derived only from this encounter's PHQ-9 answer (E6).
  const selfHarmPositive = isSelfHarmPositive(
    questionnaireResponses[QUESTIONNAIRE_URLS.PHQ9]?.responses ?? [],
  );
  const administeredUrls = new Set([
    ...Object.keys(questionnaireResponses),
    ...Object.keys(persistedSettlements),
  ]);
  const adaptiveSections = hasRestoredSession
    ? composePersistedAdaptiveSession({
        reasons: persistedSession?.reasons ?? {},
        administeredUrls,
        selfHarmPositive,
      }).sections
    : freshComposition.sections;

  // Reason each adaptive instrument was asked (including the trigger-added
  // suicide-risk + safety-plan), so the builder can tag each QR for grouped
  // read-back (F7) and cross-visit memory.
  const adaptiveReasons: Record<string, AdaptiveSectionReasonCode> =
    persistedSession?.reasons ? { ...persistedSession.reasons } : {};
  const urlToSectionKey: Record<string, AdaptiveSectionKey> = {};
  for (const section of [...adaptiveSections, ...TRIGGER_ADDED_SECTIONS]) {
    for (const instrument of section.instruments) {
      const url =
        QUESTIONNAIRE_URLS[instrument as keyof typeof QUESTIONNAIRE_URLS];
      if (!hasRestoredSession) {
        adaptiveReasons[url] = section.reasonCode;
      } else if (
        selfHarmPositive &&
        section.reasonCode === "triggered" &&
        adaptiveReasons[url] === undefined
      ) {
        // A corrected PHQ-9 can newly trigger C-SSRS/Safety Plan while editing
        // a restored sitting. Add only those new reasons; the original
        // composition remains otherwise immutable.
        adaptiveReasons[url] = section.reasonCode;
      }
      urlToSectionKey[url] = section.key;
    }
  }

  // Rehydrate explicit section settlements (declines / clinician-judgement) from
  // the form so they survive the rail remounting (e.g. toggling off and on) and
  // are not reset (D8). Only explicit decline/judgement decisions are restored:
  // "answered" and "pending" are re-derived from responses, so restoring them
  // would make a not-yet-settled section read as settled after a remount —
  // hiding its decline controls and breaking undo.
  const initialSettlements: Partial<
    Record<AdaptiveSectionKey, AdaptiveSectionStatus>
  > = {};
  for (const [url, status] of Object.entries(persistedSettlements)) {
    if (!isExplicitSettlement(status)) continue;
    if (conflictedQuestionnaireUrls.has(url)) continue;
    const key = urlToSectionKey[url];
    if (key) initialSettlements[key] = status;
  }

  // On by default for adults (D10); turning it off is one action, no
  // justification, and reveals the existing questionnaire picker.
  //
  // The off choice is persisted on the form (`_adaptiveActive`) so it can
  // survive this step unmounting/remounting on step navigation. Restoring it is
  // gated on there being state to protect — a restored session, or classic
  // picker answers already entered — so a fresh sitting still defaults on. This
  // matters because when the rail silently flips back on, any classic-picker
  // instrument the clinician administered (e.g. a Safety Plan, available to all
  // users) is hidden, and the trigger-drop effect below would wipe it: the
  // clinician returns to a blank Safety Plan they had mostly completed.
  const hasEnteredClassicResponses = (
    currentValues.selectedQuestionnaires ?? []
  ).some(
    (url) =>
      ((currentValues.questionnaireResponses ?? {})[url]?.responses.length ??
        0) > 0,
  );
  const [adaptiveOff, setAdaptiveOff] = useState(
    () =>
      currentValues._adaptiveActive === false &&
      (hasRestoredSession || hasEnteredClassicResponses),
  );
  const adaptiveShown = adaptiveActive && !adaptiveOff;
  const [assessmentRemovalBlocked, setAssessmentRemovalBlocked] =
    useState(false);
  const [promotedAdaptiveUrls, setPromotedAdaptiveUrls] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const handleAdaptiveToggle = (checked: boolean) => {
    const turningOff = !checked;
    if (turningOff) {
      const enteredUrls = Object.entries(questionnaireResponses)
        .filter(
          ([url, entry]) =>
            !conflictedQuestionnaireUrls.has(url) && entry.responses.length > 0,
        )
        .map(([url]) => url);
      if (enteredUrls.length > 0) {
        setPromotedAdaptiveUrls(new Set(enteredUrls));
        form.setFieldValue(
          "selectedQuestionnaires" as never,
          [...new Set([...selectedQuestionnaires, ...enteredUrls])] as never,
        );
      }
    } else {
      setPromotedAdaptiveUrls(new Set());
    }
    setAdaptiveOff(turningOff);
  };

  // Keep the step validator in sync with whether the adaptive screener is
  // active (so recording is gated on settlement + acknowledgement, C6 + D13),
  // guarded against re-render loops.
  useEffect(() => {
    const values = form.values as Record<string, unknown>;
    if (values._adaptiveActive !== adaptiveShown) {
      form.setFieldValue("_adaptiveActive" as never, adaptiveShown as never);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adaptiveShown]);

  // Persist the adaptive session metadata so the encounter bundle tags each
  // adaptive QR with the shared session id + reason (D18/F7) and the baseline
  // flag (E1). Waits for memory to resolve so a returning patient is never
  // stored as baseline, and re-runs whenever the resolved memory changes the
  // desired session (baseline flag or the reason/instrument map).
  const adaptiveReasonsSignature = JSON.stringify(adaptiveReasons);
  useEffect(() => {
    const values = form.values as Record<string, unknown>;
    const current = values._adaptiveScreenerSession as
      | {
          id: string;
          baseline: boolean;
          reasons: Record<string, AdaptiveSectionReasonCode>;
        }
      | undefined;

    if (!adaptiveShown) {
      const hasClinicalState =
        Object.values(questionnaireResponses).some(
          (entry) => entry.responses.length > 0,
        ) ||
        Object.keys(persistedSettlements).length > 0 ||
        Boolean(
          values._suicideRiskAcknowledged ||
          values._suicideRiskAcknowledgedAt ||
          values._suicideRiskAcknowledgedBy,
        );
      if (current !== undefined && !hasClinicalState && !hasRestoredSession) {
        form.setFieldValue(
          "_adaptiveScreenerSession" as never,
          undefined as never,
        );
      }
      return;
    }

    if (hasRestoredSession) {
      const desired = current
        ? { ...current, reasons: adaptiveReasons }
        : persistedSession;
      if (JSON.stringify(current ?? null) !== JSON.stringify(desired ?? null)) {
        form.setFieldValue(
          "_adaptiveScreenerSession" as never,
          desired as never,
        );
      }
      return;
    }

    // Preserve the persisted session while cross-visit memory resolves so draft
    // saves during load still tag adaptive-only instruments.
    if (memory.loading) {
      return;
    }

    const desired = {
      id: current?.id ?? adaptiveSessionId,
      baseline: memory.isBaseline,
      reasons: adaptiveReasons,
    };
    if (JSON.stringify(current ?? null) !== JSON.stringify(desired ?? null)) {
      form.setFieldValue("_adaptiveScreenerSession" as never, desired as never);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adaptiveShown,
    memory.loading,
    memory.isBaseline,
    adaptiveSessionId,
    adaptiveReasonsSignature,
    hasRestoredSession,
  ]);

  // While memory is still loading, block recording so the screening can't be
  // finalized against the baseline default composition (medium-severity race).
  useEffect(() => {
    if (adaptiveShown && memory.loading) {
      const values = form.values as Record<string, unknown>;
      if (values._adaptiveCanRecord !== false) {
        form.setFieldValue("_adaptiveCanRecord" as never, false as never);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adaptiveShown, memory.loading]);

  // Adaptive clinical state is not cleared when the screener is toggled off.
  // The session remains provenance for entered responses and terminal
  // settlements; only a completely unused transient session is discarded.

  // Suicide risk is re-derived from today's self-harm answer only (E6). If that
  // answer is reverted to non-positive, drop only unsaved trigger-section state.
  // Persisted responses and their acknowledgement provenance are historical
  // clinical data and must remain intact on edit.
  useEffect(() => {
    if (!adaptiveShown || selfHarmPositive) return;
    const values = form.values as Record<string, unknown>;
    const persistedIds = (values._questionnaireResponseIds ?? {}) as Record<
      string,
      string
    >;
    const current = (values.questionnaireResponses ?? {}) as Record<
      string,
      unknown
    >;
    // Instruments the clinician deliberately administered through the classic
    // picker (or promoted off the rail) are their own selection, not an
    // auto-inserted trigger section — their answers are real clinical data and
    // must never be dropped as "unsaved trigger state". A Safety Plan is
    // administrable to all users independent of any suicide-risk trigger, so
    // wiping it on a non-positive self-harm answer is silent data loss.
    const selected = (values.selectedQuestionnaires ?? []) as string[];
    const triggeredUrls = TRIGGER_ADDED_SECTIONS.flatMap(
      (section) => section.instruments,
    ).map((key) => QUESTIONNAIRE_URLS[key as keyof typeof QUESTIONNAIRE_URLS]);
    if (triggeredUrls.some((url) => current[url] !== undefined)) {
      const next = { ...current };
      for (const url of triggeredUrls) {
        if (
          !persistedIds[url] &&
          !selected.includes(url) &&
          !conflictedQuestionnaireUrls.has(url)
        ) {
          delete next[url];
        }
      }
      form.setFieldValue("questionnaireResponses" as never, next as never);
    }
    const hasPersistedSuicideRiskResponse =
      persistedIds[QUESTIONNAIRE_URLS.C_SSRS] !== undefined ||
      conflictedQuestionnaireUrls.has(QUESTIONNAIRE_URLS.C_SSRS);
    if (
      !hasPersistedSuicideRiskResponse &&
      (values._suicideRiskAcknowledged ||
        values._suicideRiskAcknowledgedAt ||
        values._suicideRiskAcknowledgedBy)
    ) {
      form.setFieldValue(
        "_suicideRiskAcknowledged" as never,
        undefined as never,
      );
      form.setFieldValue(
        "_suicideRiskAcknowledgedAt" as never,
        undefined as never,
      );
      form.setFieldValue(
        "_suicideRiskAcknowledgedBy" as never,
        undefined as never,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adaptiveShown, selfHarmPositive, scoreConflictSignature]);

  const handleRecordEligibility = (canRecord: boolean) => {
    const values = form.values as Record<string, unknown>;
    if (values._adaptiveCanRecord !== canRecord) {
      form.setFieldValue("_adaptiveCanRecord" as never, canRecord as never);
    }
  };

  // The suicide-risk acknowledgement (D13) lives on the form so it survives the
  // rail unmounting.
  const suicideRiskAcknowledged = Boolean(
    (form.values as Record<string, unknown>)._suicideRiskAcknowledged,
  );
  const handleAcknowledgedChange = (acknowledged: boolean) => {
    if (conflictedQuestionnaireUrls.has(QUESTIONNAIRE_URLS.C_SSRS)) return;
    form.setFieldValue(
      "_suicideRiskAcknowledged" as never,
      acknowledged as never,
    );
    // Capture when the advocate acknowledged (who is the acting practitioner,
    // stamped at save time) so the record carries who/when (D13).
    form.setFieldValue(
      "_suicideRiskAcknowledgedAt" as never,
      (acknowledged ? new Date().toISOString() : undefined) as never,
    );
    if (!acknowledged) {
      form.setFieldValue(
        "_suicideRiskAcknowledgedBy" as never,
        undefined as never,
      );
    }
  };

  const handleSettlementsChange = (byUrl: Record<string, string>) => {
    const values = form.values as Record<string, unknown>;
    const current = values._adaptiveSettlements as
      Record<string, string> | undefined;
    const protectedSettlements = { ...byUrl };
    for (const url of conflictedQuestionnaireUrls) {
      if (current?.[url] !== undefined) {
        protectedSettlements[url] = current[url]!;
      } else {
        delete protectedSettlements[url];
      }
    }
    if (
      JSON.stringify(current ?? {}) !== JSON.stringify(protectedSettlements)
    ) {
      form.setFieldValue(
        "_adaptiveSettlements" as never,
        protectedSettlements as never,
      );
    }
  };

  const shouldShowCSSRSAlert = useMemo(() => {
    if (cssrsAlertDismissed) return false;
    const responses = (form.values as AssessmentsCapableFormData)
      .questionnaireResponses?.[QUESTIONNAIRE_URLS.PHQ9];
    if (!responses?.completed) return false;
    const q9 = responses.responses.find((r) => r.linkId === "phq9-9");
    return q9?.value !== undefined && q9.value > 0;
  }, [form.values, cssrsAlertDismissed]);

  const assessmentsError = (form.errors as Record<string, unknown>)[
    "assessments"
  ] as string | undefined;
  const hasReadOnlyArtifacts =
    (currentValues._readOnlyQuestionnaireCount ?? 0) > 0 || hasScoreConflicts;
  const hasSuicideRiskConflict = conflictedQuestionnaireUrls.has(
    QUESTIONNAIRE_URLS.C_SSRS,
  );
  const readOnlyWarning = hasReadOnlyArtifacts ? (
    <Alert
      color={hasSuicideRiskConflict ? "red" : "orange"}
      icon={<IconAlertTriangle size={16} />}
    >
      {hasSuicideRiskConflict
        ? "The recorded suicide-risk assessment has conflicting scores and is read-only. Clinical-data review is required before this intake can be completed."
        : "Some linked assessments are read-only and will not be changed."}
    </Alert>
  ) : null;

  // The off toggle is locked while any suicide-risk obligation is open, so
  // recording can never be bypassed by switching the screener off (D9/D13). A
  // positive PHQ-9 self-harm answer triggers C-SSRS: the toggle stays locked
  // until C-SSRS is completed, and — if C-SSRS is positive — acknowledged. A
  // completed, negative C-SSRS resolves the obligation and unlocks the toggle.
  const cssrsPositive =
    selfHarmPositive &&
    (questionnaireResponses[QUESTIONNAIRE_URLS.C_SSRS]?.responses ?? []).some(
      (r) => (r.value ?? 0) > 0,
    );
  const cssrsSettled =
    selfHarmPositive &&
    (questionnaireResponses[QUESTIONNAIRE_URLS.C_SSRS]?.completed === true ||
      isExplicitSettlement(
        persistedSettlements[QUESTIONNAIRE_URLS.C_SSRS] ?? "pending",
      ));
  const safetyLock = isScreenerOffLocked({
    selfHarmPositive,
    suicideRiskCompleted: cssrsSettled,
    suicideRiskPositive: cssrsPositive,
    suicideRiskAcknowledged,
  });
  const screenerOffLocked = safetyLock || hasSuicideRiskConflict;

  const classicAssessments = (
    <>
      {assessmentRemovalBlocked && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={16} />}
          mb="sm"
          onClose={() => setAssessmentRemovalBlocked(false)}
          withCloseButton
        >
          Entered or recorded assessment answers cannot be removed here.
        </Alert>
      )}
      <QuestionnaireSelector
        value={selectedQuestionnaires}
        disabled={disabled}
        onChange={(selectedUrls) => {
          const preservedConflictSelections = selectedQuestionnaires.filter(
            (url) => conflictedQuestionnaireUrls.has(url),
          );
          const allowedSelectedUrls = selectedUrls.filter(
            (url) => !conflictedQuestionnaireUrls.has(url),
          );
          const attemptedConflictChange =
            selectedUrls.some(
              (url) =>
                conflictedQuestionnaireUrls.has(url) &&
                !selectedQuestionnaires.includes(url),
            ) ||
            preservedConflictSelections.some(
              (url) => !selectedUrls.includes(url),
            );
          const persistedIds = currentValues._questionnaireResponseIds ?? {};
          const protectedUrls = Object.keys(questionnaireResponses).filter(
            (url) =>
              Boolean(persistedIds[url]) || promotedAdaptiveUrls.has(url),
          );
          const blockedUrls = protectedUrls.filter(
            (url) =>
              selectedQuestionnaires.includes(url) &&
              !selectedUrls.includes(url),
          );
          const effectiveSelectedUrls = [
            ...new Set([
              ...allowedSelectedUrls,
              ...preservedConflictSelections,
              ...blockedUrls,
            ]),
          ];
          setAssessmentRemovalBlocked(
            attemptedConflictChange || blockedUrls.length > 0,
          );
          form.setFieldValue(
            "selectedQuestionnaires" as never,
            effectiveSelectedUrls as never,
          );

          const initialized: Record<
            string,
            EncounterQuestionnaireResponseEntry
          > = { ...questionnaireResponses };

          for (const url of effectiveSelectedUrls) {
            if (!initialized[url]) {
              initialized[url] = { responses: [], completed: false };
            }
          }
          for (const url of Object.keys(initialized)) {
            if (
              !effectiveSelectedUrls.includes(url) &&
              !conflictedQuestionnaireUrls.has(url)
            ) {
              delete initialized[url];
            }
          }

          for (const targetUrl of effectiveSelectedUrls) {
            const pairs =
              SIMPLE_PREFILL[targetUrl as keyof typeof SIMPLE_PREFILL];
            if (!pairs) continue;
            const target = initialized[targetUrl] ?? {
              responses: [],
              completed: false,
            };
            const targetHas = new Set(target.responses.map((r) => r.linkId));
            for (const { fromUrl, from, to } of pairs) {
              const source = initialized[fromUrl];
              if (!source || targetHas.has(to)) continue;
              const src = source.responses.find((r) => r.linkId === from);
              if (src && src.value !== undefined) {
                target.responses.push({
                  linkId: to,
                  value: src.value,
                  coding: src.coding,
                });
                targetHas.add(to);
              }
            }
            initialized[targetUrl] = {
              responses: target.responses,
              completed: false,
            };
          }

          form.setFieldValue(
            "questionnaireResponses" as never,
            initialized as never,
          );
        }}
      />

      {assessmentsError && selectedQuestionnaires.length === 0 && (
        <FormField error={assessmentsError}>
          <div />
        </FormField>
      )}

      {shouldShowCSSRSAlert && (
        <Alert
          color="orange"
          radius="md"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          withCloseButton
          onClose={() => setCssrsAlertDismissed(true)}
          mb="md"
        >
          <Text fw={600} size="sm" mb={4}>
            Patient indicated suicidal ideation on PHQ-9
          </Text>
          <Text size="sm">
            Clinical guidance recommends completing the Columbia-Suicide
            Severity Rating Scale (C-SSRS) to properly assess suicide risk.
          </Text>
        </Alert>
      )}

      {selectedQuestionnaires.length > 0 && (
        <FormSection
          title="Assessment Administration"
          subtitle="Complete the selected standardized assessments"
        >
          <Stack gap="xl">
            {selectedQuestionnaires.map((questionnaireUrl) => {
              const responses = questionnaireResponses[questionnaireUrl] ?? {
                responses: [],
                completed: false,
              };
              return (
                <QuestionnaireRenderer
                  key={questionnaireUrl}
                  questionnaireUrl={questionnaireUrl}
                  responses={responses}
                  disabled={
                    disabled ||
                    conflictedQuestionnaireUrls.has(questionnaireUrl)
                  }
                  showValidationErrors={!!assessmentsError}
                  onChange={(newResponses) => {
                    if (conflictedQuestionnaireUrls.has(questionnaireUrl)) {
                      return;
                    }
                    const updated = {
                      ...questionnaireResponses,
                      [questionnaireUrl]: newResponses,
                    };
                    form.setFieldValue(
                      "questionnaireResponses" as never,
                      updated as never,
                    );
                  }}
                  showScore={responses.completed}
                  // Attribution is a property of the instrument, not the flow:
                  // licensed credit lines (e.g. WSAS Mundt et al.) must render
                  // wherever the instrument is administered, including the
                  // manual picker path — not only the adaptive rail (D2).
                  showAttribution
                />
              );
            })}

            {assessmentsError && (
              <FormField error={assessmentsError}>
                <div />
              </FormField>
            )}
          </Stack>
        </FormSection>
      )}
    </>
  );

  if (!adaptiveActive) {
    if (confirmedAdult) {
      return (
        <Stack gap="sm">
          {readOnlyWarning}
          {classicAssessments}
        </Stack>
      );
    }
    // Adaptive screener is adults-only (D14): when the patient is not a
    // confirmed adult (under 18, or an unknown/unverifiable birth date), it is
    // shown disabled and the existing picker is used.
    return (
      <Stack gap="sm">
        {readOnlyWarning}
        <Text size="sm" c="dimmed" data-testid="adaptive-adults-only-note">
          Adaptive screener — Adults (18+) only. The standard questionnaire
          picker is shown instead.
        </Text>
        {classicAssessments}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {readOnlyWarning}
      <Group justify="space-between">
        <Text fw={600} size="sm">
          Adaptive screener
        </Text>
        <Switch
          checked={!adaptiveOff}
          disabled={
            disabled ||
            screenerOffLocked ||
            (hasRestoredSession && currentEncounterStatus === "finished")
          }
          data-testid="adaptive-screener-toggle"
          label={adaptiveOff ? "Off" : "On"}
          onChange={(event) =>
            handleAdaptiveToggle(event.currentTarget.checked)
          }
        />
      </Group>
      {screenerOffLocked && (
        <Text size="xs" c="red.7" data-testid="adaptive-safety-lock-note">
          {hasSuicideRiskConflict
            ? "Clinical-data review is required before switching the screener off."
            : "Complete and acknowledge the suicide-risk screen before switching the screener off."}
        </Text>
      )}
      {adaptiveOff ? (
        classicAssessments
      ) : memory.loading ? (
        <Text size="sm" c="dimmed" data-testid="adaptive-memory-loading">
          Loading screening history…
        </Text>
      ) : (
        <>
          {memory.degraded && (
            <Text size="xs" c="orange.7" data-testid="adaptive-memory-degraded">
              Screening history could not be loaded — additions may be
              incomplete.
            </Text>
          )}
          <AdaptiveScreenerStep
            sections={adaptiveSections}
            questionnaireResponses={questionnaireResponses}
            initialSettlements={initialSettlements}
            disabled={disabled}
            readOnlyQuestionnaireUrls={conflictedQuestionnaireUrls}
            duplicateScoreQuestionnaireUrls={duplicateScoreQuestionnaireUrls}
            acknowledged={suicideRiskAcknowledged}
            onSuicideRiskAcknowledgedChange={handleAcknowledgedChange}
            onRecordEligibilityChange={handleRecordEligibility}
            onSettlementsChange={handleSettlementsChange}
            onResponsesChange={(url, entry) => {
              if (conflictedQuestionnaireUrls.has(url)) return;
              // Read the latest responses from the form (not the render closure)
              // so back-to-back updates to different instruments don't clobber
              // one another and drop answers.
              const latest =
                (form.getValues() as AssessmentsCapableFormData)
                  .questionnaireResponses ?? {};
              form.setFieldValue(
                "questionnaireResponses" as never,
                { ...latest, [url]: entry } as never,
              );
            }}
          />
        </>
      )}
    </Stack>
  );
};
